// Defra reporting dashboard (admin): where every receipt stands with Defra, and the
// lists that need a person — due soon, overdue, unknown outcomes, failed, missing
// information, amendments and receipts deleted here but reported to Defra.

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Settings } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Spinner } from '../../components/ui/Spinner/Spinner';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { Pagination } from '../../components/ui/Pagination/Pagination';
import {
  clearDwtPause,
  fetchDwtDashboardList,
  fetchDwtOverview,
  type DwtDashboardKind,
  type DwtDashboardRow,
  type DwtOverview,
} from '../../api/dwt';
import { useToast } from '../../hooks/useToast';
import { DwtDueBadge, DwtStatusBadge } from './components/DwtBadges';
import { ResolveUnknownDialog } from './components/ResolveUnknownDialog';
import { countdownText, formatDateTime } from './dwtLabels';
import styles from './dwt.module.css';

const MODE_TEXT: Record<DwtOverview['mode'], string> = {
  off: 'Off: nothing is reported',
  manual: 'Manual: you queue or submit receipts yourself',
  auto_on_approval: 'Automatic: receipts are reported as they are approved',
};

interface Tile {
  kind: DwtDashboardKind;
  label: string;
  count: (o: DwtOverview) => number;
  tone?: 'warn' | 'bad';
  empty: string;
}

const TILES: Tile[] = [
  { kind: 'overdue', label: 'Overdue', count: (o) => o.counts.overdue, tone: 'bad', empty: 'Nothing is overdue.' },
  { kind: 'due_24h', label: 'Due within 24 hours', count: (o) => o.counts.due_within_24h, tone: 'warn', empty: 'Nothing is due in the next 24 hours.' },
  { kind: 'unknown_outcome', label: 'Check with Defra', count: (o) => o.counts.unknown_outcome, tone: 'bad', empty: 'No receipts are waiting for you to check with Defra.' },
  { kind: 'failed_permanent', label: 'Failed', count: (o) => o.counts.failed_permanent, tone: 'bad', empty: 'No receipts have failed.' },
  { kind: 'blocked_missing_data', label: 'Missing information', count: (o) => o.counts.blocked_missing_data, tone: 'warn', empty: 'No receipts are missing information.' },
  { kind: 'amendment_required', label: 'Amendments waiting', count: (o) => o.counts.amendment_required, tone: 'warn', empty: 'No amendments are waiting.' },
  { kind: 'orphaned', label: 'Deleted but reported', count: (o) => o.counts.orphaned, tone: 'warn', empty: 'No reported receipts have been deleted.' },
];

const LIST_TITLE: Record<DwtDashboardKind, string> = {
  overdue: 'Overdue: past the deadline for reporting to Defra',
  due_24h: 'Due within 24 hours',
  unknown_outcome: 'Check with Defra: we could not confirm whether Defra received these',
  failed_permanent: 'Failed: Defra did not accept these',
  blocked_missing_data: 'Missing information: add it, then queue the receipt again',
  amendment_required: 'Amendments waiting: receipts changed after they were reported',
  orphaned: 'Deleted here but reported to Defra',
};

function isKind(v: string | null): v is DwtDashboardKind {
  return TILES.some((t) => t.kind === v);
}

export function DwtDashboardPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const kindParam = params.get('kind');
  const kind: DwtDashboardKind = isKind(kindParam) ? kindParam : 'overdue';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const [clearOpen, setClearOpen] = useState(false);
  const [resolveTarget, setResolveTarget] = useState<DwtDashboardRow | null>(null);

  const overview = useQuery({ queryKey: ['dwt', 'overview'], queryFn: fetchDwtOverview, refetchInterval: 60_000, retry: false });
  const list = useQuery({
    queryKey: ['dwt', 'dashboard', kind, page],
    queryFn: () => fetchDwtDashboardList(kind, page),
    retry: false,
    enabled: overview.data !== undefined,
  });

  const clear = useMutation({
    mutationFn: clearDwtPause,
    onSuccess: (r) => {
      toast.success(
        r.cleared
          ? `Pause cleared. ${r.released_submissions} held ${r.released_submissions === 1 ? 'receipt' : 'receipts'} will be retried.`
          : 'The company was not paused.',
      );
      setClearOpen(false);
      refreshAll();
    },
    onError: () => toast.error('Could not clear the pause.'),
  });

  function refreshAll() {
    void qc.invalidateQueries({ queryKey: ['dwt'] });
  }

  function setKind(k: DwtDashboardKind) {
    const next = new URLSearchParams(params);
    next.set('kind', k);
    next.delete('page');
    setParams(next);
  }

  if (overview.isLoading) {
    return (
      <div className={styles.stack} style={{ padding: '0 1rem 2rem' }}>
        <PageHeader title="Defra reporting" />
        <Card>
          <div className={styles.row}>
            <Spinner /> <span>Loading…</span>
          </div>
        </Card>
      </div>
    );
  }
  if (overview.isError || !overview.data) {
    return (
      <div className={styles.stack} style={{ padding: '0 1rem 2rem' }}>
        <PageHeader title="Defra reporting" />
        <Card>
          <p role="alert">The dashboard could not be loaded.</p>
        </Card>
      </div>
    );
  }

  const o = overview.data;
  const tile = TILES.find((t) => t.kind === kind)!;
  const rows = list.data?.rows ?? [];

  return (
    <div className={styles.stack} style={{ padding: '0 1rem 2rem' }}>
      <PageHeader
        title="Defra reporting"
        subtitle="Digital Waste Tracking: receipts reported to Defra, and what needs attention."
        actions={
          <Button variant="secondary" icon={Settings} onClick={() => navigate('/settings/dwt')}>
            Settings
          </Button>
        }
      />

      {o.paused.active ? (
        <div className={`${styles.banner} ${styles.bannerDanger}`} role="alert" data-testid="pause-banner">
          <span className={styles.bannerTitle}>
            <AlertTriangle size={16} aria-hidden /> Reporting is paused
          </span>
          <span>{o.paused.explanation}</span>
          {o.paused.since ? <span className={styles.muted}>Paused since {formatDateTime(o.paused.since)}.</span> : null}
          <div className={styles.row}>
            <Button variant="danger" onClick={() => setClearOpen(true)}>
              Clear pause
            </Button>
          </div>
        </div>
      ) : null}

      {!o.on ? (
        <div className={`${styles.banner} ${styles.bannerInfo}`} role="status">
          <span className={styles.bannerTitle}>Reporting to Defra is switched off</span>
          <span>
            {o.configured
              ? 'Turn it on in the settings to start reporting receipts.'
              : 'It has not been set up for this company yet.'}{' '}
            <Link to="/settings/dwt">Open settings</Link>
          </span>
        </div>
      ) : null}

      <Card title="Status">
        <dl className={styles.facts}>
          <dt className={styles.factLabel}>Mode</dt>
          <dd className={styles.factValue}>{MODE_TEXT[o.mode]}</dd>
          <dt className={styles.factLabel}>Environment</dt>
          <dd className={styles.factValue}>{o.environment === 'production' ? 'Production (live reporting)' : 'Test'}</dd>
          <dt className={styles.factLabel}>Start date</dt>
          <dd className={styles.factValue}>{o.start_date ?? 'Not set'}</dd>
          <dt className={styles.factLabel}>Last worker run</dt>
          <dd className={styles.factValue}>{o.last_worker_run_at ? formatDateTime(o.last_worker_run_at) : 'Not run yet'}</dd>
          <dt className={styles.factLabel}>Next retry</dt>
          <dd className={styles.factValue}>
            {o.next_retry_at ? formatDateTime(o.next_retry_at) : 'Nothing waiting to be retried'}
            {o.queued > 0 ? ` (${o.queued} queued)` : ''}
          </dd>
        </dl>
      </Card>

      <div className={styles.statGrid} role="group" aria-label="Counts">
        <div className={styles.stat} aria-label="Not yet reported">
          <div className={styles.statValue}>{o.counts.not_reported}</div>
          <div className={styles.statLabel}>Not yet reported</div>
        </div>
        <div className={styles.stat} aria-label="Reported to Defra">
          <div className={styles.statValue}>{o.counts.by_status['submitted'] ?? 0}</div>
          <div className={styles.statLabel}>Reported to Defra</div>
        </div>
        {TILES.map((t) => (
          <button
            key={t.kind}
            type="button"
            className={`${styles.stat} ${t.kind === kind ? styles.statActive : ''}`}
            onClick={() => setKind(t.kind)}
            aria-pressed={t.kind === kind}
            aria-label={`${t.label}: ${t.count(o)}`}
          >
            <div className={styles.statValue}>{t.count(o)}</div>
            <div className={styles.statLabel}>{t.label}</div>
          </button>
        ))}
      </div>

      <Card title={LIST_TITLE[kind]}>
        {list.isLoading ? (
          <div className={styles.row}>
            <Spinner /> <span>Loading…</span>
          </div>
        ) : list.isError ? (
          <p role="alert">This list could not be loaded.</p>
        ) : rows.length === 0 ? (
          <p className={styles.muted}>{tile.empty}</p>
        ) : (
          <table className={styles.table} aria-label={tile.label}>
            <thead>
              <tr>
                <th>Receipt</th>
                <th>Vehicle</th>
                <th>Status</th>
                <th>{kind === 'orphaned' ? 'What happened' : 'Due and details'}</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.receipt_enc_id}>
                  <td>
                    {kind === 'orphaned' ? (
                      r.unique_id
                    ) : (
                      <Link to={`/collections/receipts/${encodeURIComponent(r.receipt_enc_id)}`}>{r.unique_id}</Link>
                    )}
                    {r.receipt_date ? <div className={styles.muted}>{r.receipt_date}</div> : null}
                  </td>
                  <td>{r.car_registration ?? '—'}</td>
                  <td>
                    {kind === 'orphaned' ? (
                      <span>
                        {r.waste_tracking_id ? <>WT ID {r.waste_tracking_id}</> : '—'}
                      </span>
                    ) : (
                      <div className={styles.stack}>
                        <DwtStatusBadge status={r.dwt_status} />
                        {r.failure_code === 'movement_not_found' ? <span className={styles.small}>Defra could not find the movement</span> : null}
                      </div>
                    )}
                  </td>
                  <td>
                    {kind === 'orphaned' ? (
                      <div className={styles.stack}>
                        <span>{r.note}</span>
                        <span className={styles.muted}>
                          Reason: {r.reason ?? '—'} · Deleted by {r.deleted_by ?? 'unknown'} · {formatDateTime(r.updated_at)}
                        </span>
                      </div>
                    ) : (
                      <div className={styles.stack}>
                        {r.due_by ? (
                          <span className={styles.row}>
                            <DwtDueBadge state={r.due_state} />
                            <span className={styles.muted}>
                              {formatDateTime(r.due_by)} ({countdownText(r.due_by)})
                            </span>
                          </span>
                        ) : null}
                        {r.messages.slice(0, 2).map((m, i) => (
                          <span key={`${m.severity}-${i}`} className={styles.small}>
                            {m.field_label ? `${m.field_label}: ` : ''}
                            {m.message}
                          </span>
                        ))}
                        {r.next_retry_at ? <span className={styles.muted}>Next retry {formatDateTime(r.next_retry_at)}</span> : null}
                      </div>
                    )}
                  </td>
                  <td>
                    {kind === 'unknown_outcome' ? (
                      <Button size="sm" variant="secondary" onClick={() => setResolveTarget(r)}>
                        Resolve
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {list.data && list.data.pagination.totalPages > 1 ? (
          <Pagination
            pagination={list.data.pagination}
            onPageChange={(p) => {
              const next = new URLSearchParams(params);
              next.set('page', String(p));
              setParams(next);
            }}
          />
        ) : null}
      </Card>

      <ConfirmModal
        open={clearOpen}
        title="Clear the pause?"
        message="Only do this once the service charge has been paid with Defra. Held receipts will be tried again straight away, and if Defra still says the charge is unpaid, reporting pauses again."
        confirmLabel="Clear pause"
        cancelLabel="Cancel"
        variant="danger"
        loading={clear.isPending}
        onCancel={() => setClearOpen(false)}
        onConfirm={() => clear.mutate()}
      />

      <ResolveUnknownDialog
        open={resolveTarget !== null}
        receipt={resolveTarget}
        onClose={() => setResolveTarget(null)}
        onResolved={refreshAll}
      />
    </div>
  );
}

export default DwtDashboardPage;
